// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LA NUBE · LAS SUBIDAS VIVEN POR ENCIMA DE LAS PANTALLAS (1-oct-2026, B-1013)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  LO QUE PIDIÓ EL DUEÑO, literal: «al subir un archivo manualmente a la nube desde /nube, debe permitirme moverme por
//  la app sin ningún problema y poder trabajar libremente una vez yo ya he seleccionado que documento quiero subir, si
//  no, es una locura trabajar».
//
//  LO QUE PASABA: la cola de subidas (utils/nubeSubidas · crearCola) se creaba DENTRO de pages/Nube.jsx. Al ir a la
//  agenda o a una ficha, la página se desmontaba y con ella se iban la cola, su barra y el aviso de «hay subidas sin
//  terminar»: las peticiones en vuelo seguían a ciegas y lo que estaba en espera no se subía nunca.
//
//  AHORA: UNA cola por sesión, aquí, en un módulo (no en una página ni en un diseño de pantalla: la aplicación tiene tres
//  y cambiar de uno a otro desmonta lo que cuelga de ellos). La pinta en TODA la aplicación
//  components/nube/SubidasGlobales.jsx (montado en App.jsx, junto a las rutas) y la página de la Nube la usa para añadir.
//
//  PURO: ni React, ni el DOM, ni services/api. Quien arranca la aplicación le dice con qué API se sube y cómo se lee la
//  identidad de la sesión (`configurarSubidas`); las guardias lo ejecutan tal cual (routes/nube-subidas-segundo-plano).
//
//  LA REGLA QUE NO SE NEGOCIA: UNA COLA ES DE UNA SESIÓN Y DE UNA CLÍNICA. Un archivo elegido en una clínica no puede
//  acabar en la Nube de otra, y una carpeta «principal» (sin número) es la de la clínica en la que se esté EN ESE MOMENTO.
//  Así que la identidad (quién y en qué clínica) se apunta al crear la cola y:
//   · cada petición de la cola la vuelve a mirar ANTES de salir: si ya no es la misma, NO SALE (y la cola se abandona);
//   · cerrar sesión, entrar con otra cuenta y entrar o salir de una clínica la abandonan en el momento (sin una petición
//     más) y se avisa de cuántas subidas se han cancelado. Lo que quedó abierto en el servidor caduca solo en 24 horas.
import { crearCola } from './nubeSubidas'
import { t, marca, tDato } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

let conf = { api: null, identidad: null }
let actual = null          // { id, base, cola }
let plegado = true         // el panel: plegado es la pastilla; desplegado, la lista
const oyentes = new Set()
const alCambiar = new Set()

function avisar() {
  for (const f of [...oyentes]) { try { f() } catch (_) { /* un oyente roto no puede parar las subidas */ } }
}

function idDeAhora() {
  try { return conf.identidad ? String(conf.identidad() || '') : '' } catch (_) { return '' }
}

// Con qué API se sube y cómo se lee la identidad de la sesión («quién:en qué clínica»; '' = sin sesión). Una vez, al
// arrancar la aplicación.
export function configurarSubidas({ api = null, identidad = null } = {}) {
  conf = { api, identidad }
}

// La API de una cola, atada a SU identidad: si la sesión o la clínica han cambiado, la petición no sale.
function atar(api, id) {
  const paso = (metodo) => (...args) => {
    if (idDeAhora() !== id) {
      revisarSesion()
      return Promise.reject(Object.assign(new Error('La sesión ha cambiado: la subida se ha cancelado.'), { cancelada: true }))
    }
    return api[metodo](...args)
  }
  return { get: paso('get'), post: paso('post'), put: paso('put'), delete: paso('delete') }
}

// ¿Sigue siendo la sesión de la cola? Si no, se abandona (sin peticiones) y se avisa a quien escuche de cuántas subidas
// se han cancelado. Devuelve null si no ha cambiado nada, o { canceladas }.
export function revisarSesion() {
  if (!actual || actual.id === idDeAhora()) return null
  const vieja = actual
  actual = null
  plegado = true
  let canceladas = 0
  try { canceladas = vieja.cola.abandonar() } catch (_) { /* abandonar no lanza */ }
  for (const f of [...alCambiar]) { try { f({ canceladas }) } catch (_) { /* nada */ } }
  avisar()
  return { canceladas }
}

// La cola de ESTA sesión (se crea la primera vez).
// `api`: solo cuenta si nadie ha configurado una (la página montada sola, en una guardia). Ahí no hay sesión que cuidar y
// la cola es de quien la pide: una por API, sin abandonar la de nadie (dos páginas montadas a la vez, cada una con su API
// de mentira, se quitarían la cola la una a la otra sin parar).
const sinConfigurar = new WeakMap()
export function colaDeLaSesion({ api = null } = {}) {
  if (!conf.api) {
    if (!api) throw new Error(t('utils.nubeSubidasGlobal.nubesubidasglobal_falta_la_api_con', 'nubeSubidasGlobal: falta la API con la que se sube'))
    let c = sinConfigurar.get(api)
    if (!c) { c = crearCola({ api, alCambiar: avisar }); sinConfigurar.set(api, c) }
    return c
  }
  revisarSesion()
  if (!actual) {
    const id = idDeAhora()
    actual = { id, cola: crearCola({ api: atar(conf.api, id), alCambiar: avisar }) }
  }
  return actual.cola
}

// La cola si ya existe (sin crearla): para quien solo mira.
export function colaSiHay() {
  revisarSesion()
  return actual ? actual.cola : null
}

export function suscribirSubidas(fn) { oyentes.add(fn); return () => { oyentes.delete(fn) } }
export function alCancelarPorCambio(fn) { alCambiar.add(fn); return () => { alCambiar.delete(fn) } }

// ¿Queda algo sin terminar (subiendo, en espera o en pausa)? Es lo que pregunta el aviso de «hay subidas en marcha».
export function haySubidasSinTerminar() {
  return !!(actual && actual.cola.quedaAlgo())
}
export function cuantasSinTerminar() {
  return actual ? Number(actual.cola.estado().sinTerminar) || 0 : 0
}

// Cancelar todas avisando al servidor (antes de cerrar sesión: todavía con la identidad de la cola). Devuelve cuántas.
export async function cancelarTodasLasSubidas() {
  if (!actual) return 0
  return actual.cola.cancelarTodo()
}

// El panel: la página de la Nube lo despliega al añadir archivos; al salir de la Nube se pliega a la pastilla.
export const subidasPlegadas = () => plegado
export function plegarSubidas(v) {
  const n = !!v
  if (n === plegado) return
  plegado = n
  avisar()
}

// ── CERRAR SESIÓN CON SUBIDAS EN MARCHA ─────────────────────────────────────────────────────────
// Lo llama el botón «Cerrar sesión» ANTES de salir. Sin subidas, deja pasar. Con subidas, pregunta (`preguntar` es el
// diálogo de la aplicación: lo pone quien arranca); si se acepta, las cancela avisando al servidor y deja pasar.
export const TXT_SALIR_TITULO = marca('utils.nubeSubidasGlobal.hay_subidas_a_la_nube', 'Hay subidas a la Nube en marcha')
export function textoAlSalir(n, que = marca('utils.nubeSubidasGlobal.cierras_sesion', 'cierras sesión')) {
  return t('utils.nubeSubidasGlobal.si_lo_que_ya_se', '{{x}}. Si {{que}}, {{x2}}: lo que ya se ha subido del todo se queda en la Nube.', { x: (t('utils.nubeSubidasGlobal.plural_hay_archivos_que_todavia', { one: 'Hay {{count}} archivo que todavía se está subiendo a la Nube', other: 'Hay {{count}} archivos que todavía se están subiendo a la Nube' }, { count: n, _forma: (n === 1) ? 'one' : 'other' })), que: tDato(que), x2: (t('utils.nubeSubidasGlobal.plural_se_cancelan', { one: 'se cancela', other: 'se cancelan' }, { count: n, _forma: (n === 1) ? 'one' : 'other' })) })
}
let preguntar = null
export function ponerPregunta(fn) { preguntar = typeof fn === 'function' ? fn : null }
export async function salirConSubidas(que = marca('utils.nubeSubidasGlobal.cierras_sesion', 'cierras sesión')) {
  const n = cuantasSinTerminar()
  if (!n) return true
  let si = true
  if (preguntar) {
    try { si = (await preguntar({ titulo: tDato(TXT_SALIR_TITULO), mensaje: textoAlSalir(n, que), textoConfirmar: t('utils.nubeSubidasGlobal.cancelar_las_subidas_y_salir', 'Cancelar las subidas y salir'), textoCancelar: t('utils.nubeSubidasGlobal.seguir_subiendo', 'Seguir subiendo') })) === true } catch (_) { si = false }
  }
  if (!si) return false
  // Como mucho 4 segundos esperando al servidor: salir no se puede quedar colgado de una red caída.
  await Promise.race([cancelarTodasLasSubidas().catch(() => 0), new Promise((ok) => setTimeout(ok, 4000))])
  return true
}

// Solo para las guardias: empezar de cero.
export function _reiniciarParaPruebas() {
  if (actual) { try { actual.cola.abandonar() } catch (_) { /* nada */ } }
  actual = null
  plegado = true
  conf = { api: null, identidad: null }
  preguntar = null
  oyentes.clear()
  alCambiar.clear()
}
